import React from 'react';
import { getContext } from 'common';

import Styled from './styles';

interface LabelContextInterface {
  disabled?: boolean;
  readOnly?: boolean;
}

const LabelContext = React.createContext<LabelContextInterface | null>({});

export interface LabelProps extends Omit<React.ComponentPropsWithRef<'label'>, 'content'> {
  /** The content of the label such as, `<TextInputLabelContent>`, or the content of the label and a form control such as `<TextInput>`. */
  children: React.ReactNode;
  /** If `true`, the children will be rendered in column direction. E.g. Set to `true` when TextField is nested. */
  verticalLayout?: boolean;
  /** If `true`, the max-width will be `31.25rem`. */
  maxWidth?: boolean;
  /** If `true`, the nested field component (TextInput and TextArea) appears disabled. */
  disabled?: boolean;
  /** Set to `true` if the form-related element is readOnly. */
  readOnly?: boolean;
}

/** `Label` component can be used to render `label` element in the forms. Any other props than the `Label` props will be provided to the root element (`label` native element). */
const Label = React.forwardRef<HTMLElement, LabelProps>((props: LabelProps, ref) => getContext(({ RTL }) => {
  const {
    children,
    ...otherProps
  } = props;
  const wrapperProps = {
    ...otherProps,
    disabled: undefined,
    $disabled: otherProps.disabled
  };
  return (
    <LabelContext.Provider value={otherProps}>
      <Styled.Wrapper
        ref={ref}
        RTL={RTL}
        {...wrapperProps}
      >
        {children}
      </Styled.Wrapper>
    </LabelContext.Provider>
  );
}));

Label.displayName = 'Label';

Label.defaultProps = {
  verticalLayout: false,
  maxWidth: false,
  disabled: false,
  readOnly: false,
};

export { Label as default, LabelContext };
